/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Gift, Tag, Share2, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { walletApi, gbp } from "@/lib/wallet";
import SEO from "@/components/site/SEO";

export default function CustomerEarn() {
  const [refData, setRefData] = useState(null);
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [recipient, setRecipient] = useState({ email: "", name: "", amount: 5, message: "" });
  const [gifting, setGifting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await walletApi.get("/customer/referrals/me");
        setRefData(data);
      } catch (err) {
        console.error("Could not load referral data", err);
      }
    })();
  }, []);

  const shareUrl = refData ? `${window.location.origin}${refData.share_url}` : "";

  async function copyLink() {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy link"); }
  }

  async function redeem(e) {
    e.preventDefault();
    if (!code.trim()) return;
    setRedeeming(true);
    try {
      const { data } = await walletApi.post("/customer/promo/redeem", { code: code.trim().toUpperCase() });
      toast.success(`Credited ${gbp(data.credited_gbp)} from ${data.code}`);
      setCode("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not redeem");
    } finally { setRedeeming(false); }
  }

  async function sendGift(e) {
    e.preventDefault();
    if (!recipient.email || !recipient.name || recipient.amount <= 0) return;
    setGifting(true);
    try {
      const { data } = await walletApi.post("/customer/gift/create", {
        recipient_email: recipient.email.trim().toLowerCase(),
        recipient_name: recipient.name.trim(),
        amount: parseFloat(recipient.amount),
        message: recipient.message?.trim() || undefined,
      });
      toast.success(`Gift sent to ${data.recipient_email}`);
      setRecipient({ email: "", name: "", amount: 5, message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gift failed");
    } finally { setGifting(false); }
  }

  return (
    <div className="min-h-screen bg-brand-bgSoft text-brand-ink" data-testid="customer-earn">
      <SEO title="Earn & Gift" path="/app/earn" />
      <header className="bg-white border-b border-brand-line">
        <div className="container-page flex items-center justify-between h-16">
          <Link to="/app" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/80 hover:text-brand-ink" data-testid="earn-back">
            <ArrowLeft className="h-4 w-4" /> Back to dashboard
          </Link>
        </div>
      </header>

      <div className="container-page py-10 grid lg:grid-cols-2 gap-6">
        {/* Refer & Earn */}
        <section className="rounded-2xl bg-brand-ink text-white p-8 relative overflow-hidden" data-testid="earn-refer-card">
          <div className="absolute inset-0 bg-grid-overlay opacity-30" />
          <div className="relative">
            <p className="label-eyebrow text-brand-green">Refer & earn</p>
            <h1 className="mt-3 font-display font-black text-3xl">Give £2, get £2</h1>
            <p className="mt-2 text-white/70 text-sm max-w-md">Share your link. When your friend tops up £10 or more, both wallets get £2 instantly.</p>

            {refData && (
              <>
                <div className="mt-6 flex items-center gap-2">
                  <input
                    readOnly value={shareUrl} data-testid="earn-refer-link"
                    className="flex-1 rounded-lg bg-white/10 border border-white/20 px-3 py-2 text-sm text-white font-mono"
                  />
                  <button onClick={copyLink} data-testid="earn-copy-btn"
                    className="rounded-full bg-white text-brand-ink px-4 py-2 text-xs font-semibold inline-flex items-center gap-1.5">
                    {copied ? <><Check className="h-3.5 w-3.5" />Copied</> : <><Copy className="h-3.5 w-3.5" />Copy</>}
                  </button>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-3 text-center text-xs">
                  <Stat label="Pending" value={refData.stats.pending} />
                  <Stat label="Qualified" value={refData.stats.qualified} />
                  <Stat label="Earned" value={gbp(refData.stats.earned_gbp)} />
                </div>
                <div className="mt-5">
                  <a target="_blank" rel="noopener noreferrer"
                    href={`https://wa.me/?text=${encodeURIComponent(`Try OneDial Africa with my link — we both get £2 when you top up: ${shareUrl}`)}`}
                    data-testid="earn-share-whatsapp"
                    className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] px-4 py-2 text-xs font-bold text-white">
                    <Share2 className="h-3.5 w-3.5" /> Share on WhatsApp
                  </a>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Promo redeem */}
        <section className="rounded-2xl border border-brand-line bg-white p-6" data-testid="earn-promo-card">
          <p className="label-eyebrow">Promo code</p>
          <h2 className="mt-2 font-display font-extrabold text-2xl">Got a code?</h2>
          <p className="text-sm text-brand-muted mt-1">Redeem a OneDial promo code straight into your wallet.</p>
          <form onSubmit={redeem} className="mt-5 flex gap-2">
            <input
              type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="WELCOME10" maxLength={32}
              data-testid="earn-promo-input"
              className="flex-1 rounded-lg border border-brand-line bg-white px-3 py-2 text-sm font-mono focus:outline-none focus:border-brand-green"
            />
            <button type="submit" disabled={redeeming || !code.trim()} data-testid="earn-promo-submit"
              className="rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 text-white px-5 py-2 text-sm font-semibold inline-flex items-center gap-1.5">
              <Tag className="h-3.5 w-3.5" /> {redeeming ? "Redeeming…" : "Redeem"}
            </button>
          </form>
          <p className="mt-3 text-xs text-brand-muted">Each promo code can be used once per account.</p>
        </section>

        {/* Gift credit */}
        <section className="lg:col-span-2 rounded-2xl border border-brand-line bg-white p-6" data-testid="earn-gift-card">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-brand-greenDark" />
            <p className="label-eyebrow">Gift credit</p>
          </div>
          <h2 className="mt-2 font-display font-extrabold text-2xl">Send credit to a friend or family member</h2>
          <p className="text-sm text-brand-muted mt-1">We'll email them a one-time claim link. The amount is debited from your wallet immediately.</p>
          <form onSubmit={sendGift} className="mt-5 grid sm:grid-cols-2 gap-3">
            <input required type="text" value={recipient.name} onChange={(e) => setRecipient({...recipient, name: e.target.value})}
              placeholder="Recipient name" data-testid="gift-name"
              className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <input required type="email" value={recipient.email} onChange={(e) => setRecipient({...recipient, email: e.target.value})}
              placeholder="Recipient email" data-testid="gift-email"
              className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <input required type="number" min="1" max="200" step="0.01" value={recipient.amount}
              onChange={(e) => setRecipient({...recipient, amount: e.target.value})}
              placeholder="Amount (GBP)" data-testid="gift-amount"
              className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <input type="text" maxLength={240} value={recipient.message}
              onChange={(e) => setRecipient({...recipient, message: e.target.value})}
              placeholder="Optional message" data-testid="gift-message"
              className="rounded-lg border border-brand-line bg-white px-3 py-2 text-sm focus:outline-none focus:border-brand-green" />
            <div className="sm:col-span-2 flex items-center gap-3">
              <button type="submit" disabled={gifting} data-testid="gift-submit"
                className="rounded-full bg-brand-green hover:bg-brand-greenHover disabled:opacity-60 text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5">
                <Gift className="h-4 w-4" /> {gifting ? "Sending…" : "Send gift"}
              </button>
              <p className="text-xs text-brand-muted">Gifts are debited from your wallet immediately and credited to the recipient when they claim.</p>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-white/10 border border-white/20 px-3 py-3">
      <p className="text-white/60 text-[10px] uppercase tracking-wider">{label}</p>
      <p className="font-display font-black text-lg mt-0.5">{value}</p>
    </div>
  );
}
